<template>
  <section :class="['site-search', { 'container search-page': !compact }]" :aria-label="compact ? 'Site search' : undefined">
    <template v-if="!compact">
      <h1>Search SLMath</h1>
      <p class="search-intro">Find seminars, workshops, programs, summer schools, and public events.</p>
    </template>
    <form class="search-form" role="search" @submit.prevent="submit">
      <label :for="inputId" class="sr-only">Search SLMath</label>
      <input :id="inputId" v-model="searchText" type="search" name="q" minlength="2" maxlength="160" required autocomplete="off" :placeholder="compact ? 'Search SLMath' : 'Search by title, topic, speaker, or organizer'">
      <button type="submit" class="search-submit">Search</button>
    </form>
    <template v-if="!compact">
      <div class="search-filters">
        <div>
          <label :for="`${inputId}-period`">When</label>
          <select :id="`${inputId}-period`" v-model="period" @change="changeFilters">
            <option value="all">Any time</option>
            <option value="upcoming">Current &amp; upcoming</option>
            <option value="past">Past</option>
          </select>
        </div>
        <form @submit.prevent="changeFilters">
          <label :for="`${inputId}-year`">Year</label>
          <div class="search-year">
            <input :id="`${inputId}-year`" v-model="year" type="number" min="1900" max="2200" placeholder="Any year">
            <button type="submit">Apply</button>
          </div>
        </form>
        <button v-if="category !== 'all' || period !== 'all' || year" type="button" class="clear-filters" @click="clearFilters">Clear filters</button>
      </div>
      <div class="search-categories" aria-label="Content type">
        <button v-for="item in categories" :key="item.value" type="button" :aria-pressed="category === item.value" :class="{ selected: category === item.value, 'video-category': item.value === 'videos' }" @click="selectCategory(item.value)">
          {{ item.label }}<span v-if="hasSearched && !loading && !error"> ({{ counts[item.value] || 0 }})</span>
        </button>
      </div>
      <div class="search-status" aria-live="polite" aria-atomic="true">
        <p v-if="loading">Searching…</p>
        <p v-else-if="error" role="alert">{{ error }} <button type="button" @click="loadResults">Try again</button></p>
        <p v-else-if="!hasSearched">Enter a title, a mathematical topic, or a name to begin.</p>
        <template v-else>
          <p v-if="total">{{ total.toLocaleString() }} {{ total === 1 ? 'result' : 'results' }} for “{{ appliedQuery }}”<span v-if="totalPages > 1"> · Page {{ page }} of {{ totalPages }}</span></p>
          <p v-else>No {{ category === 'videos' ? 'videos' : 'events or programs' }} found for “{{ appliedQuery }}”. Try fewer words, a different spelling, or clear the filters.</p>
          <p v-if="category === 'all' && counts.videos" class="video-suggestion"><button type="button" @click="selectCategory('videos')">See {{ counts.videos.toLocaleString() }} matching {{ counts.videos === 1 ? 'video' : 'videos' }}</button></p>
        </template>
      </div>
      <ol v-if="!loading && !error" class="search-results" :start="(page - 1) * 20 + 1">
        <li v-for="result in results" :key="result.url" class="search-result">
          <div class="result-meta"><span>{{ categoryLabel(result.category) }}</span><span v-if="result.start_date">{{ dateRange(result) }}</span></div>
          <h2><router-link :to="result.url">{{ result.title }}</router-link></h2>
          <p v-if="result.description">{{ result.description }}</p>
        </li>
      </ol>
      <nav v-if="!loading && !error && totalPages > 1" class="search-pagination" aria-label="Search results pages">
        <button type="button" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <button type="button" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
      </nav>
    </template>
  </section>
</template>

<script>
import siteSearchClient from '@/utils/siteSearchClient'
const categories = [
	{ value: 'all', label: 'All events & programs' },
	{ value: 'seminars', label: 'Seminars' },
	{ value: 'workshops', label: 'Workshops' },
	{ value: 'programs', label: 'Programs' },
	{ value: 'summer_schools', label: 'Summer schools' },
	{ value: 'public_events', label: 'Public events' },
	{ value: 'videos', label: 'Videos' },
]

export default {
	name: 'Search',
	props: { type: { type: String, default: '' } },
	data() {
		return { searchText: '', appliedQuery: '', category: 'all', period: 'all', year: '', page: 1, results: [], counts: {}, total: 0, totalPages: 0, loading: false, error: '', hasSearched: false, requestId: 0, categories }
	},
	computed: {
		compact() { return this.type === 'dashboard' },
		inputId() { return `site-search-${this._uid}` },
	},
	watch: {
		'$route.query': {
			immediate: true,
			handler(query = {}) {
				this.searchText = typeof query.q === 'string' ? query.q : ''
				if (this.compact) return
				this.appliedQuery = this.searchText.trim()
				this.category = categories.some(item => item.value === query.category) ? query.category : 'all'
				this.period = ['all', 'upcoming', 'past'].includes(query.period) ? query.period : 'all'
				this.year = typeof query.year === 'string' ? query.year : ''
				this.page = /^\d{1,5}$/.test(query.page || '') ? Math.max(1, Number(query.page)) : 1
				this.loadResults()
			},
		},
	},
	beforeDestroy() { this.requestId += 1 },
	methods: {
		submit() {
			const q = this.searchText.trim()
			if (q.length < 2) return
			this.navigate({ q, category: this.compact ? 'all' : this.category, period: this.compact ? 'all' : this.period, year: this.compact ? '' : this.year, page: 1 })
		},
		navigate(values) {
			const query = { q: values.q }
			if (values.category !== 'all') query.category = values.category
			if (values.period !== 'all') query.period = values.period
			if (values.year) query.year = String(values.year)
			if (values.page > 1) query.page = String(values.page)
			if (this.$route.path === '/search' && JSON.stringify(query) === JSON.stringify(this.$route.query)) {
				if (!this.compact) this.loadResults()
				return
			}
			this.$router.push({ path: '/search', query })
		},
		changeFilters() { this.goToPage(1) },
		selectCategory(value) { this.category = value; this.changeFilters() },
		clearFilters() { this.category = 'all'; this.period = 'all'; this.year = ''; this.changeFilters() },
		goToPage(page) { this.navigate({ q: this.appliedQuery, category: this.category, period: this.period, year: this.year, page }) },
		async loadResults() {
			const id = ++this.requestId
			this.error = ''
			this.results = []
			this.hasSearched = Boolean(this.appliedQuery)
			this.loading = false
			if (!this.hasSearched) { this.counts = {}; this.total = 0; this.totalPages = 0; return }
			this.loading = true
			try {
				const { data } = await siteSearchClient.get('/site_search', { params: { q: this.appliedQuery, category: this.category, period: this.period, year: this.year, page: this.page } })
				if (id !== this.requestId) return
				if (!Array.isArray(data?.results) || !data?.counts) throw new Error('Invalid search response')
				this.results = data.results
				this.counts = data.counts
				this.total = data.total
				this.totalPages = data.total_pages
				this.page = data.page
			} catch (error) {
				if (id === this.requestId) this.error = error.response?.data?.error || 'Search is temporarily unavailable. Please try again.'
			} finally {
				if (id === this.requestId) this.loading = false
			}
		},
		categoryLabel(value) { return categories.find(item => item.value === value)?.label || 'Event' },
		dateRange(result) {
			const format = value => new Date(`${value}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
			const start = format(result.start_date)
			return result.end_date && result.end_date !== result.start_date ? `${start} – ${format(result.end_date)}` : start
		},
	},
}
</script>

<style scoped lang="scss">
.site-search { color: #252125; }
.search-page { padding-top: 2.5rem; padding-bottom: 4rem; min-height: 65vh; }
.search-page h1 { margin-bottom: .7rem; }
.search-intro { color: #5b555e; margin-bottom: 1.5rem; }
.search-form { display: flex; width: 100%; gap: 0; }
.search-page .search-form { max-width: 850px; }
.search-form input { min-width: 0; flex: 1; border: 1px solid #827886; border-radius: 3px 0 0 3px; padding: .65rem .8rem; background: white; color: #252125; }
.site-search button, .site-search input, .site-search select { font: inherit; }
.site-search button { cursor: pointer; }
.site-search button:focus-visible, .site-search input:focus-visible, .site-search select:focus-visible, .site-search a:focus-visible { outline: 3px solid #8b00bc; outline-offset: 3px; }
.search-submit { background: #252125; color: white; border: 1px solid #252125; border-radius: 0 3px 3px 0; padding: .65rem 1rem; }
.search-filters { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 1rem; margin: 1.5rem 0; }
.search-filters label { display: block; font-weight: 600; margin-bottom: .35rem; }
.search-filters select, .search-year input, .search-year button, .search-pagination button { border: 1px solid #a59ca8; border-radius: 3px; background: white; padding: .5rem .7rem; min-height: 42px; }
.search-year { display: flex; gap: .4rem; }
.search-year input { width: 125px; }
.clear-filters { border: 0; background: none; padding: .6rem 0; color: #710099; text-decoration: underline; }
.search-categories { display: flex; flex-wrap: wrap; gap: .5rem; padding-bottom: 1.25rem; border-bottom: 1px solid #ded8e1; }
.search-categories button { border: 1px solid #c9bfce; border-radius: 4px; padding: .55rem .75rem; background: white; color: #514658; min-height: 44px; }
.search-categories button.selected { background: #710099; color: white; border-color: #710099; }
.search-categories .video-category { margin-left: auto; }
.search-status { margin: 1.5rem 0 .5rem; }
.search-status button { background: none; border: 0; padding: 0; color: #710099; text-decoration: underline; }
.search-results { list-style: none; padding: 0; margin: 0; max-width: 960px; }
.search-result { border-bottom: 1px solid #e4dee7; padding: 1.4rem 0; }
.result-meta { display: flex; flex-wrap: wrap; gap: .4rem 1.2rem; color: #615667; font-size: .9rem; margin-bottom: .5rem; }
.result-meta span:first-child { font-weight: 600; }
.search-result h2 { font-size: 1.4rem; line-height: 1.35; margin: 0 0 .5rem; }
.search-result h2 a { color: #710099; }
.search-result p { margin: 0; line-height: 1.6; overflow-wrap: anywhere; }
.search-pagination { display: flex; align-items: center; gap: 1rem; margin-top: 2rem; }
.search-pagination button:disabled { opacity: .45; cursor: default; }
@media (max-width: 600px) {
  .search-page { padding-top: 1.5rem; }
  .search-categories .video-category { margin-left: 0; }
  .search-form input { font-size: 16px; }
  .search-result h2 { font-size: 1.2rem; }
}
</style>
